import React, { useEffect, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft } from "react-icons/fa";
import styles from "./GenerateInvoicePage.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import Select from "react-select";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { selectStyles, toastStyles } from "../../static/styles";
import { toast } from "react-toastify";
import { GET, POST } from "../../services/axios.service";
import {
  OrdersProps,
  OptionsProps,
  IInvoiceFormInput,
} from "../../types/orders";
import PhotoModel from "../../components/orders/PhotoModel";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getIsOrderPhotoModalOpen,
  toggleOrderPhotoModal,
} from "../../features/orders/ordersSlice";
import moment from "moment";

const GenerateInvoicePage = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isOrderPhotoModalOpen = useAppSelector(getIsOrderPhotoModalOpen);

  const state = location.state as {
    selectedOrders: OrdersProps[];
  } | null;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemOptions, setItemOptions] = useState<OptionsProps[]>([]);

  // Redirect if no state (direct URL access or refresh)
  useEffect(() => {
    if (!state?.selectedOrders || state.selectedOrders.length === 0) {
      navigate(route.orders, { replace: true });
    }
  }, []);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response: any = await GET("/v1/admin/items", { all: true });
        if (response.data.status === "success") {
          setItemOptions(
            response.data.data.records.map((item: any) => ({
              value: item.id,
              label: item.name,
              in_inch: item.in_inch === 1,
            }))
          );
        }
      } catch {
        // Failed to fetch items
      }
    };
    fetchItems();
  }, []);

  const selectedOrders = state?.selectedOrders || [];

  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IInvoiceFormInput>({
    defaultValues: {
      orders: selectedOrders.map((order) => {
        const isInch = order.item?.in_inch === 1;
        return {
          order_id: order.id,
          item_id: order.item_id,
          name: order.name || "",
          delivered_qty: "" as any,
          inch: isInch ? (order.inch || ("" as any)) : 1,
          rate: order.rate || ("" as any),
          close_order: false,
          pending: "" as any,
        };
      }),
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "orders",
  });

  const watchOrders = watch("orders");

  const partyName = selectedOrders[0]?.party?.firm_name || "";

  const handleExportPaymentPdf = async (paymentId: number) => {
    try {
      await GET(`/v1/admin/payments/${paymentId}/reports`, {})
        .then(async (response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });

            const downloadLink = response.data.data.download_link;
            try {
              const newWindow = window.open(downloadLink, "_blank");
              if (
                !newWindow ||
                newWindow.closed ||
                typeof newWindow.closed == "undefined"
              ) {
                throw new Error("Pop-up blocked");
              }
            } catch (e) {
              window.location.href = downloadLink;
            }
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("Something went wrong. Please try again.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleInvoiceSubmit = async (data: IInvoiceFormInput) => {
    setIsSubmitting(true);
    try {
      const response: any = await POST(`/v1/admin/orders/close`, {
        orders: data.orders,
      });

      if (response.data.status === "success") {
        toast.success(response.data.message, {
          ...(toastStyles as any),
        });

        const paymentId = response.data.payment_id;
        if (paymentId) {
          await handleExportPaymentPdf(paymentId);
        }

        navigate(route.orders, { replace: true });
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message || "Something went wrong. Please try again.",
        {
          ...(toastStyles as any),
        }
      );
    }
    setIsSubmitting(false);
  };

  const handleGoBack = () => {
    navigate(route.orders);
  };

  const handleOpenPhoto = (imageUrl: string) => {
    dispatch(toggleOrderPhotoModal(imageUrl));
  };

  const preventWheelScroll = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.addEventListener(
      "wheel",
      function (e) {
        e.preventDefault();
      },
      { passive: false }
    );
  };

  if (!state?.selectedOrders || state.selectedOrders.length === 0) {
    return null;
  }

  return (
    <Layout>
      <div className={styles.GenerateInvoicePage}>
        <div className="logoSection">
          <h1
            onClick={handleGoBack}
            className="text-5xl font-bold text-white lg:text-3xl cursor-pointer lg:w-fit"
          >
            {companyName}
          </h1>

          <hr className="w-[180px] border-white border-[4px] rounded-[4px] mx-auto mt-4 lg:mx-0 lg:w-24 lg:border-[2px] lg:mt-2" />
        </div>

        <div className="pageTitleSection">
          <FaArrowLeft
            onClick={handleGoBack}
            className="lg:hidden absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full cursor-pointer"
          />

          <h1 className="text-5xl mt-6 lg:mt-0 font-bold text-white lg:text-2xl lg:text-primary-1">
            Generate Invoice
          </h1>
        </div>

        <div className="mt-[60px] px-10 py-5">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handleGoBack}
                className="hidden lg:flex items-center gap-2 text-primary-1 font-semibold hover:opacity-80 transition-opacity"
              >
                <FaArrowLeft /> Back to Orders
              </button>
              <p className="text-primary-1 font-bold text-lg">
                Party: {partyName}
              </p>
            </div>
            <p className="text-primary-1 font-bold">
              {selectedOrders.length} order(s)
            </p>
          </div>

          <form onSubmit={handleSubmit(handleInvoiceSubmit)}>
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
             <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Sr. No.
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Photo
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Process
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Prev. Delivered
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider min-w-[200px]">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Return Qty
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Inch
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Close
                    </th>
                    <th className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider">
                      Pending
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fields.map((field, index) => {
                    const order = selectedOrders[index];
                    const hasExistingItem = !!order.item_id;
                    const selectedItemId = watchOrders?.[index]?.item_id;
                    const selectedItem = itemOptions.find(
                      (opt) => opt.value === selectedItemId
                    );
                    const isInchEditable = !hasExistingItem && selectedItem?.in_inch === true;

                    return (
                      <tr key={field.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                          {index + 1}
                          <input
                            type="hidden"
                            {...register(
                              `orders.${index}.order_id` as const
                            )}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {order.image ? (
                            <img
                              src={order.image}
                              alt={`Order ${order.id}`}
                              className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => handleOpenPhoto(order.image)}
                            />
                          ) : (
                            <span className="text-gray-400 text-xs">
                              No photo
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-semibold">{order.id}</div>
                          <div>Qty: {order.qty}</div>
                          <div>{moment(order.created_at).format("DD/MM/YYYY")}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.process?.name || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>Count: {order.delivered_qty}</div>
                          <div>Last: {order.last_delivery_date ? moment(order.last_delivery_date).format("DD/MM/YYYY") : "-"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm min-w-[200px]">
                          <div className={`h-10 w-full relative rounded ${hasExistingItem ? "bg-gray-300" : "bg-gradient-to-r from-primary-1 to-primary-2"}`}>
                            <Controller
                              control={control}
                              name={`orders.${index}.item_id` as const}
                              rules={{ required: true }}
                              render={({ field: selectField }) => (
                                <Select
                                  {...selectField}
                                  isDisabled={hasExistingItem}
                                  value={
                                    hasExistingItem
                                      ? { value: order.item_id, label: order.item?.name || "" }
                                      : itemOptions.find(
                                          (val) =>
                                            val.value === selectField.value
                                        ) || null
                                  }
                                  className="text-primary-1 p-0 font-semibold text-base placeholder:text-primary-2 placeholder:opacity-50 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                                  styles={selectStyles}
                                  placeholder="Select item"
                                  options={itemOptions}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  onChange={(val: any) => {
                                    setValue(
                                      `orders.${index}.item_id`,
                                      val?.value
                                    );
                                    if (!val?.in_inch) {
                                      setValue(
                                        `orders.${index}.inch`,
                                        1
                                      );
                                    } else {
                                      setValue(
                                        `orders.${index}.inch`,
                                        "" as any
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </div>
                          {errors.orders?.[index]?.item_id && (
                            <span className="text-red-500 text-xs">
                              Required
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm min-w-[200px]">
                          <div className={`h-10 w-full relative rounded ${hasExistingItem ? "bg-gray-300" : "bg-gradient-to-r from-primary-1 to-primary-2"}`}>
                            <input
                              className={`focus:outline-none px-2 py-1 font-semibold text-base absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded ${hasExistingItem ? "text-gray-500 bg-gray-100 cursor-not-allowed" : "text-primary-1 placeholder:text-primary-2 placeholder:opacity-50"}`}
                              type="text"
                              placeholder="Name"
                              readOnly={hasExistingItem}
                              {...register(
                                `orders.${index}.name` as const,
                                { required: !hasExistingItem }
                              )}
                            />
                          </div>
                          {errors.orders?.[index]?.name && (
                            <span className="text-red-500 text-xs block">
                              Required
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-10 w-28 relative rounded">
                            <input
                              className="text-primary-1 focus:outline-none px-2 py-1 font-semibold text-base placeholder:text-primary-2 placeholder:opacity-50 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                              type="number"
                              placeholder="Qty"
                              {...register(
                                `orders.${index}.delivered_qty` as const,
                                {
                                  required: true,
                                  min: 1,
                                  valueAsNumber: true,
                                }
                              )}
                              onFocus={preventWheelScroll}
                            />
                          </div>
                          {errors.orders?.[index]?.delivered_qty && (
                            <span className="text-red-500 text-xs block">
                              Min 1
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div
                            className={`h-10 w-28 relative rounded ${
                              isInchEditable
                                ? "bg-gradient-to-r from-primary-1 to-primary-2"
                                : "bg-gray-300"
                            }`}
                          >
                            <input
                              className={`focus:outline-none px-2 py-1 font-semibold text-base absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded ${
                                isInchEditable
                                  ? "text-primary-1 placeholder:text-primary-2 placeholder:opacity-50"
                                  : "text-gray-500 bg-gray-100 cursor-not-allowed"
                              }`}
                              type="number"
                              step="0.1"
                              placeholder="Inch"
                              readOnly={!isInchEditable}
                              {...register(
                                `orders.${index}.inch` as const,
                                {
                                  valueAsNumber: true,
                                  required: isInchEditable && !hasExistingItem,
                                  min: isInchEditable ? 1 : undefined,
                                }
                              )}
                              onFocus={preventWheelScroll}
                            />
                          </div>
                          {errors.orders?.[index]?.inch && (
                            <span className="text-red-500 text-xs block">
                              Required
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className={`h-10 w-28 relative rounded ${hasExistingItem ? "bg-gray-300" : "bg-gradient-to-r from-primary-1 to-primary-2"}`}>
                            <input
                              className={`focus:outline-none px-2 py-1 font-semibold text-base absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded ${hasExistingItem ? "text-gray-500 bg-gray-100 cursor-not-allowed" : "text-primary-1 placeholder:text-primary-2 placeholder:opacity-50"}`}
                              type="number"
                              step="any"
                              placeholder="Rate"
                              readOnly={hasExistingItem}
                              {...register(
                                `orders.${index}.rate` as const,
                                {
                                  required: !hasExistingItem,
                                  valueAsNumber: true,
                                }
                              )}
                              onFocus={preventWheelScroll}
                            />
                          </div>
                          {errors.orders?.[index]?.rate && (
                            <span className="text-red-500 text-xs block">
                              Required
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <input
                            type="checkbox"
                            className="w-5 h-5 accent-primary-1"
                            {...register(
                              `orders.${index}.close_order` as const
                            )}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div
                            className={`h-10 w-28 relative rounded ${
                              watchOrders?.[index]?.close_order
                                ? "bg-gradient-to-r from-primary-1 to-primary-2"
                                : "bg-gray-300"
                            }`}
                          >
                            <input
                              className={`focus:outline-none px-2 py-1 font-semibold text-base absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded ${
                                watchOrders?.[index]?.close_order
                                  ? "text-primary-1 placeholder:text-primary-2 placeholder:opacity-50"
                                  : "text-gray-500 bg-gray-100 cursor-not-allowed"
                              }`}
                              type="number"
                              placeholder="Pending"
                              readOnly={!watchOrders?.[index]?.close_order}
                              {...register(
                                `orders.${index}.pending` as const,
                                {
                                  valueAsNumber: true,
                                }
                              )}
                              onFocus={preventWheelScroll}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
             </div>
            </div>

            <div className="flex gap-4 justify-end mt-6">
              <button
                type="button"
                onClick={handleGoBack}
                className="border-2 border-primary-1 text-primary-1 bg-white hover:bg-gray-50 px-6 py-2 rounded-md font-semibold transition duration-300 ease-in-out"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-primary-1 to-primary-2 text-white px-6 py-2 rounded-md font-semibold transition duration-300 ease-in-out disabled:opacity-50"
              >
                {isSubmitting ? "Please wait..." : "Submit Invoice"}
              </button>
            </div>
          </form>

          {isOrderPhotoModalOpen !== null && <PhotoModel />}
        </div>
      </div>
    </Layout>
  );
};

export default GenerateInvoicePage;
